'use client';

import { useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useConceptHeaderData } from './use-concept-header';
import type { LayoutConfig } from '../../../../../app/(auth)/layout.config';

/**
 * Hook that combines pathname-based config with dynamic store data
 * for concept pages
 */
export function useLayoutConfig(): LayoutConfig {
  const pathname = usePathname();
  const t = useTranslations('common');
  const tNav = useTranslations('navigation');
  const tTokenBalance = useTranslations('tokenBalance');
  const tPurchaseHistory = useTranslations('purchaseHistory');
  const tPaymentHistory = useTranslations('paymentHistorySection');
  const tAccountSettings = useTranslations('accountSettings');
  const tUsageStats = useTranslations('usageStats');
  const tFeedback = useTranslations('feedback');
  const tHelp = useTranslations('help');
  const tConcept = useTranslations('concept');
  const conceptHeaderData = useConceptHeaderData();

  return useMemo(() => {
    // Project pages - iOS-style: back button + centered project name
    if (pathname.match(/^\/project\/[^/]+$/)) {
      const projectId = pathname.match(/\/project\/([^/]+)/)?.[1];
      // Use project name from store if available (set by project page)
      const projectName = conceptHeaderData.projectName || t('project');
      return {
        title: projectName,
        showBackButton: true,
        backHref: '/',
        centerTitle: true, // iOS-style centered title
        showPageMenu: false, // Menu is in the hero section
        projectId: projectId,
      };
    }

    // Concept pages - use store data for dynamic values
    if (pathname.match(/^\/project\/[^/]+\/concept\/[^/]+$/)) {
      const projectId = pathname.match(/\/project\/([^/]+)/)?.[1];

      // Use store data if available, fallback to defaults
      const projectName = conceptHeaderData.projectName || t('project');
      const conceptTitle = conceptHeaderData.conceptTitle
        ? conceptHeaderData.conceptTitle
        : conceptHeaderData.conceptSequenceNumber
          ? tConcept('conceptNumber', {
              number: conceptHeaderData.conceptSequenceNumber,
            })
          : tConcept('conceptLabel');

      return {
        // Mobile: show concept title with back button to project
        title: conceptTitle,
        conceptTitle: conceptTitle,
        // Desktop: show project name in breadcrumb
        projectName: projectName,
        projectId: projectId,
        showBackButton: true,
        backHref: `/project/${projectId}`,
        showLogo: false, // Hide logo on mobile for concept pages
        showConceptNavigation: true, // Enable concept navigation arrows in mobile header
        prevConceptId: conceptHeaderData.prevConceptId,
        nextConceptId: conceptHeaderData.nextConceptId,
        showPageMenu: true,
      };
    }

    // Albums page
    if (pathname.match(/^\/project\/[^/]+\/albums$/)) {
      const projectId = pathname.match(/\/project\/([^/]+)/)?.[1];
      return {
        title: 'Albums',
        showBackButton: true,
        backHref: `/project/${projectId}`,
        centerTitle: true,
        showPageMenu: false,
      };
    }

    // Render page - hide all mobile UI elements as it has its own fullscreen interface
    if (pathname.includes('/render')) {
      return {
        title: 'Create Render',
        showBackButton: true,
        backHref: pathname.replace('/render', ''),
        hideMobileHeader: true,
        hideMobileNavigation: true,
      };
    }

    // History page
    if (pathname === '/history') {
      return {
        title: 'History',
        showLogo: true,
      };
    }

    // Settings pages
    if (pathname.startsWith('/settings')) {
      // For subpages like /settings/billing
      if (pathname !== '/settings') {
        return {
          title: t('settings'),
          showBackButton: true,
          backHref: '/settings',
          hideFloatingActionButton: true,
        };
      }
      // For main settings page
      return {
        title: t('settings'),
        hideFloatingActionButton: true,
      };
    }

    // Subscription pages
    if (pathname.startsWith('/subscriptions')) {
      return {
        title: 'Subscriptions',
        showBackButton: true,
        backHref: '/account',
        hideFloatingActionButton: true,
      };
    }

    // Token pages
    if (pathname.startsWith('/tokens')) {
      return {
        title: tTokenBalance('pageTitle'),
        showBackButton: true,
        backHref: '/account',
        hideFloatingActionButton: true,
      };
    }

    // Account subpages with specific titles and breadcrumbs
    const accountLabel = tNav('account');

    // Help center — a top-level destination reachable from the main sidebar
    // (no longer an Account sub-page), so it mirrors /history's chrome rather
    // than carrying an account back-button / breadcrumb.
    if (pathname === '/help') {
      return {
        title: tHelp('pageTitle'),
        showLogo: true,
      };
    }

    // Help center topic subpage (/help/{topic}) — the topic title is shown in
    // the page body (it ships from the API); chrome stays generic with a back
    // link to the help center root.
    if (pathname.startsWith('/help/')) {
      return {
        title: tHelp('pageTitle'),
        showBackButton: true,
        backHref: '/help',
        hideFloatingActionButton: true,
      };
    }

    // Feedback page
    if (pathname === '/account/feedback') {
      const pageTitle = tFeedback('pageTitle');
      return {
        title: pageTitle,
        showBackButton: true,
        backHref: '/account',
        hideFloatingActionButton: true,
        breadcrumbs: [
          { label: accountLabel, href: '/account' },
          { label: pageTitle },
        ],
      };
    }

    // Token balance page
    if (pathname === '/account/tokens') {
      const pageTitle = tTokenBalance('pageTitle');
      return {
        title: pageTitle,
        showBackButton: true,
        backHref: '/account',
        hideFloatingActionButton: true,
        breadcrumbs: [
          { label: accountLabel, href: '/account' },
          { label: pageTitle },
        ],
      };
    }

    // Purchase history page
    if (pathname === '/account/purchase-history') {
      const pageTitle = tPurchaseHistory('pageTitle');
      return {
        title: pageTitle,
        showBackButton: true,
        backHref: '/account',
        hideFloatingActionButton: true,
        breadcrumbs: [
          { label: accountLabel, href: '/account' },
          { label: pageTitle },
        ],
      };
    }

    // Payments page
    if (pathname === '/account/payments') {
      const pageTitle = tPaymentHistory('title');
      return {
        title: pageTitle,
        showBackButton: true,
        backHref: '/account',
        hideFloatingActionButton: true,
        breadcrumbs: [
          { label: accountLabel, href: '/account' },
          { label: pageTitle },
        ],
      };
    }

    // Usage stats page
    if (pathname === '/account/usage-stats') {
      const pageTitle = tUsageStats('title');
      return {
        title: pageTitle,
        showBackButton: true,
        backHref: '/account',
        hideFloatingActionButton: true,
        breadcrumbs: [
          { label: accountLabel, href: '/account' },
          { label: pageTitle },
        ],
      };
    }

    // Language page
    if (pathname === '/account/language') {
      const pageTitle = tAccountSettings('language.title');
      return {
        title: pageTitle,
        showBackButton: true,
        backHref: '/account',
        hideFloatingActionButton: true,
        breadcrumbs: [
          { label: accountLabel, href: '/account' },
          { label: pageTitle },
        ],
      };
    }

    // Appearance page
    if (pathname === '/account/appearance') {
      const pageTitle = tAccountSettings('theme.title');
      return {
        title: pageTitle,
        showBackButton: true,
        backHref: '/account',
        hideFloatingActionButton: true,
        breadcrumbs: [
          { label: accountLabel, href: '/account' },
          { label: pageTitle },
        ],
      };
    }

    // Branding page
    if (pathname === '/account/branding') {
      const pageTitle = tAccountSettings('renderSettings.title');
      return {
        title: pageTitle,
        showBackButton: true,
        backHref: '/account',
        hideFloatingActionButton: true,
        breadcrumbs: [
          { label: accountLabel, href: '/account' },
          { label: pageTitle },
        ],
      };
    }

    // Delete account page
    if (pathname === '/account/delete') {
      const pageTitle = tAccountSettings('deleteAccount.title');
      return {
        title: pageTitle,
        showBackButton: true,
        backHref: '/account',
        hideFloatingActionButton: true,
        breadcrumbs: [
          { label: accountLabel, href: '/account' },
          { label: pageTitle },
        ],
      };
    }

    // Fallback for other account subpages
    if (pathname.startsWith('/account/')) {
      return {
        title: accountLabel,
        showBackButton: true,
        backHref: '/account',
        hideFloatingActionButton: true,
      };
    }

    if (pathname === '/account') {
      return {
        title: accountLabel,
        showLogo: true,
        hideFloatingActionButton: true,
      };
    }

    // Default (Dashboard)
    return {
      title: 'GetFacade',
    };
  }, [
    pathname,
    conceptHeaderData,
    t,
    tNav,
    tTokenBalance,
    tPurchaseHistory,
    tPaymentHistory,
    tAccountSettings,
    tUsageStats,
    tFeedback,
    tHelp,
    tConcept,
  ]);
}
